import { resolvePageTheme } from '~/utils/resolvePageTheme';
import { ref } from 'vue';
import type { UseContentfulPageResponseModel } from '~/composables/useContentfulPage';

function isRecoverableContentfulPayloadError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }

  return 'statusCode' in error || 'statusMessage' in error;
}

/**
 * Sole owner of `/api/contentful` for CMS routes (`useContentfulPage`).
 * Exposes the resolved payload on `nuxtApp.$contentfulPagePayload` for layout,
 * analytics, and CMS pages (`useContentfulPagePayload`) — one fetch per navigation.
 */
export default defineNuxtPlugin({
  name: 'contentful-page-payload',
  async setup() {
    const contentfulPagePayload = await useContentfulPageFromRoute().catch(
      (error: unknown) => {
        // The page route consumes the shared contentful error state and throws
        // the final Nuxt error (e.g. 403) so `error.vue` can render consistently.
        if (isRecoverableContentfulPayloadError(error)) {
          return ref<UseContentfulPageResponseModel | null>(null);
        }

        throw error;
      }
    );
    const { $colorMode } = useNuxtApp();

    // Layout (PageContainer, HeroCanvas) renders before page setup; set theme here
    // so SSR markup matches the client after hydration.
    const applyPageTheme = (
      payload: typeof contentfulPagePayload.value
    ): void => {
      if (!payload?.page) {
        return;
      }

      $colorMode.value = resolvePageTheme(payload.page);
    };

    // SSR: set once. Client: watch for client navigations.
    applyPageTheme(contentfulPagePayload.value);
    if (import.meta.client) {
      watch(contentfulPagePayload, applyPageTheme, { deep: true });
    }

    return {
      provide: {
        contentfulPagePayload
      }
    };
  }
});
